import { BsdsInput } from '@bsds-lib-component/react';
import React from 'react';

import { useMaskedBsdsInput } from '../../hooks/useMaskedBsdsInput';
import type { DocumentNumberInputProps } from '../../types';
import {
  cleanDocumentAlnum,
  formatDocumentNumberInput,
  validateDocumentNumber,
} from '../../utils/documentNumberUtils';

const isDocumentComplete = (cleaned: string) => cleaned.length === 14 || /^\d{11}$/.test(cleaned);

const documentInvalidMessage = (cleaned: string) => {
  if (/^\d{11}$/.test(cleaned)) return 'CPF inválido';
  if (cleaned.length === 14) return 'CNPJ inválido';
  return 'Documento inválido';
};

export const DocumentNumberInput: React.FC<DocumentNumberInputProps> = ({
  value,
  onChange,
  onValidationChange,
  placeholder = 'CPF, CNPJ ou CNPJ alfanumérico',
  required = false,
  disabled = false,
  className = '',
  label,
  'aria-label': ariaLabel,
  'aria-describedby': ariaDescribedBy,
  'aria-invalid': ariaInvalid,
  'aria-required': ariaRequired,
  'aria-disabled': ariaDisabled,
  'aria-autocomplete': ariaAutocomplete,
  'aria-haspopup': ariaHasPopup,
  'aria-controls': ariaControls,
  'aria-owns': ariaOwns,
  'aria-activedescendant': ariaActiveDescendant,
  'aria-errormessage': ariaErrorMessage,
}) => {
  const { inputRef, errorMessage, safeValue, handleChange, handleInput } = useMaskedBsdsInput({
    value,
    onChange,
    onValidationChange,
    required,
    format: formatDocumentNumberInput,
    clean: cleanDocumentAlnum,
    isComplete: isDocumentComplete,
    validate: validateDocumentNumber,
    invalidMessage: documentInvalidMessage,
    requiredMessage: 'Documento é obrigatório',
    toDisplayValue: formatDocumentNumberInput,
  });

  return (
    <BsdsInput
      ref={inputRef}
      value={safeValue}
      onChange={handleChange}
      onInput={handleInput}
      placeholder={placeholder}
      label={label}
      required={required}
      disabled={disabled}
      className={className}
      maxlength={18}
      type="text"
      clearInput={false}
      inputMode="text"
      errorText={errorMessage}
      hasHintIcon
      color="primary"
      platform="web"
      aria-label={ariaLabel}
      aria-describedby={ariaDescribedBy}
      aria-invalid={ariaInvalid}
      aria-required={ariaRequired}
      aria-disabled={ariaDisabled}
      aria-autocomplete={ariaAutocomplete}
      aria-haspopup={ariaHasPopup}
      aria-controls={ariaControls}
      aria-owns={ariaOwns}
      aria-activedescendant={ariaActiveDescendant}
      aria-errormessage={ariaErrorMessage}
    />
  );
};
